import {
  buildCognitoryQuestionAnswerTraceEvaluationExportPath,
  buildCognitoryQuestionAnswerTraceEvaluationPath,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";
import type { CognitoryActivityEvaluationRating } from "../presentation/cognitoryActivityEvaluationStatus";
import {
  rebuildCognitoryTraceEvaluationGetPayload,
  rebuildCognitoryTraceEvaluationPutPayload,
  type CognitoryTraceEvaluationView,
} from "./cognitoryQuestionAnswerEvaluationRebuild";

export type { CognitoryTraceEvaluationView };

export async function fetchCognitoryQuestionAnswerTraceEvaluation(input: {
  cognitoryId: string;
  traceRequestId: string;
  fetchImpl?: typeof fetch;
  bearerToken?: string | null;
  apiBaseUrl?: string;
}): Promise<
  | {
      kind: "found";
      evaluation: CognitoryTraceEvaluationView;
      requestId: string;
    }
  | { kind: "no_evaluation"; requestId: string }
  | {
      kind:
        | "forbidden"
        | "temporary_failure"
        | "unauthenticated"
        | "malformed_success";
      requestId: string | null;
    }
> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }
  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  const path = buildCognitoryQuestionAnswerTraceEvaluationPath(input);
  let response: Response;
  try {
    response = await fetchImpl(`${base}${path}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      cache: "no-store",
    });
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    return { kind: "malformed_success", requestId: null };
  }
  const requestId =
    body &&
    typeof body === "object" &&
    typeof (body as { requestId?: unknown }).requestId === "string"
      ? (body as { requestId: string }).requestId
      : null;
  if (response.status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (response.status === 403) {
    return { kind: "forbidden", requestId };
  }
  if (response.status !== 200) {
    return { kind: "temporary_failure", requestId };
  }
  const rebuilt = rebuildCognitoryTraceEvaluationGetPayload({
    expectedCognitoryId: input.cognitoryId,
    expectedTraceRequestId: input.traceRequestId,
    body,
  });
  if (rebuilt.kind === "malformed_success") {
    return { kind: "malformed_success", requestId: rebuilt.supportReference };
  }
  if (rebuilt.kind === "no_evaluation") {
    return { kind: "no_evaluation", requestId: rebuilt.supportReference };
  }
  return {
    kind: "found",
    requestId: rebuilt.supportReference,
    evaluation: rebuilt.evaluation,
  };
}

export async function submitCognitoryQuestionAnswerTraceEvaluation(input: {
  cognitoryId: string;
  traceRequestId: string;
  rating: CognitoryActivityEvaluationRating;
  comment?: string;
  fetchImpl?: typeof fetch;
  bearerToken?: string | null;
  apiBaseUrl?: string;
}): Promise<
  | { kind: "saved"; requestId: string }
  | {
      kind:
        | "forbidden"
        | "temporary_failure"
        | "unauthenticated"
        | "malformed_success";
      requestId: string | null;
    }
> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }
  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  const path = buildCognitoryQuestionAnswerTraceEvaluationPath(input);
  let response: Response;
  try {
    response = await fetchImpl(`${base}${path}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        rating: input.rating,
        ...(input.comment ? { comment: input.comment } : {}),
      }),
      cache: "no-store",
    });
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    return { kind: "malformed_success", requestId: null };
  }
  const requestId =
    body &&
    typeof body === "object" &&
    typeof (body as { requestId?: unknown }).requestId === "string"
      ? (body as { requestId: string }).requestId
      : null;
  if (response.status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (response.status === 403) {
    return { kind: "forbidden", requestId };
  }
  if (response.status !== 200) {
    return { kind: "temporary_failure", requestId };
  }
  const rebuilt = rebuildCognitoryTraceEvaluationPutPayload({
    expectedCognitoryId: input.cognitoryId,
    expectedTraceRequestId: input.traceRequestId,
    body,
  });
  if (rebuilt.kind !== "recorded") {
    return { kind: "malformed_success", requestId: rebuilt.supportReference };
  }
  return { kind: "saved", requestId: rebuilt.supportReference };
}

export async function downloadCognitoryQuestionAnswerTraceEvaluationExport(input: {
  cognitoryId: string;
  fetchImpl?: typeof fetch;
  bearerToken?: string | null;
  apiBaseUrl?: string;
}): Promise<
  | { kind: "ready"; csvText: string }
  | { kind: "forbidden" | "temporary_failure" | "unauthenticated" }
> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated" };
  }
  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  const path = buildCognitoryQuestionAnswerTraceEvaluationExportPath(
    input.cognitoryId,
  );
  let response: Response;
  try {
    response = await fetchImpl(`${base}${path}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "text/csv",
      },
      cache: "no-store",
    });
  } catch {
    return { kind: "temporary_failure" };
  }
  if (response.status === 401) {
    return { kind: "unauthenticated" };
  }
  if (response.status === 403) {
    return { kind: "forbidden" };
  }
  if (response.status !== 200) {
    return { kind: "temporary_failure" };
  }
  const csvText = await response.text();
  return { kind: "ready", csvText };
}
